import { shouldNoIndexMarketPage as applyMarketPageIndexationRule } from 'constants/marketPageIndexation.mjs';
import type { MarketStatus, MarketVo } from 'models';
import type { EventVo } from 'models/event';
import {
    marketLastPriceCents,
    marketYesAskCents,
    marketYesBidCents,
    controversyScore,
    marketVolumeContracts,
} from 'models/market';
import type { SearchResultMarket } from 'models/search';
import type { SeriesVo } from 'models/series';

import { asCents } from './caseing';
import { unitStringToNumber } from './number';
import type { NumericTarget } from './numericTarget';

/**
 * Sort comparator for markets: desc by lastPrice, then asc by ticker (tie). Stable order when prices are equal.
 */
export const compareByPriceDescThenTicker = (
    a: { lastPrice: number; ticker: string },
    b: { lastPrice: number; ticker: string },
): number => b.lastPrice - a.lastPrice || a.ticker.localeCompare(b.ticker);

/**
 * Applies prioritized strikes to a list of top markets for an event
 * @param baseMarkets The initially selected top markets
 * @param allMarkets All available markets for the event
 * @param prioritizedTickers Array of prioritized market tickers from Statsig
 * @param maxMarkets Maximum number of markets to return (default 3)
 * @returns Updated list of markets with prioritized strikes included
 */
export const applyPrioritizedStrikes = <T extends { ticker: string; lastPrice: number }>(
    baseMarkets: T[],
    allMarkets: T[],
    prioritizedTickers: string[],
    maxMarkets: number = 3,
): T[] => {
    if (prioritizedTickers.length === 0) return baseMarkets;

    const result = [...baseMarkets];

    for (const ticker of prioritizedTickers) {
        const prioritizedMarket = allMarkets.find((m) => m.ticker === ticker);

        // Check if market exists, has valid price, and isn't already included
        if (prioritizedMarket && prioritizedMarket.lastPrice > 0 && !result.some((m) => m.ticker === ticker)) {
            // If we're at max capacity, replace the lowest price market
            if (result.length >= maxMarkets) {
                const lowestIndex = result.reduce(
                    (minIdx, market, idx, arr) =>
                        (market.lastPrice ?? 0) < (arr[minIdx].lastPrice ?? 0) ? idx : minIdx,
                    0,
                );
                result[lowestIndex] = prioritizedMarket;
            } else {
                result.push(prioritizedMarket);
            }
        }
    }

    // Re-sort by original market order
    result.sort((a, b) => allMarkets.indexOf(a) - allMarkets.indexOf(b));
    return result;
};

export const verifyIsSingleMarket = (markets: unknown[]): boolean => markets.length === 1;

/**
 * Checks if a market is a Tie market
 * @param market The market to check
 * @returns true if the market's subtitle is 'Tie'
 */
export const isTieMarket = (market: { subtitle: string } | null | undefined): boolean => {
    return market?.subtitle === 'Tie';
};

export const getPriceInCents = (price: number): string => {
    return price < 1 || price > 99 ? '––¢' : asCents(price);
};

export const isMarketClosed = (close_ts: string): boolean => {
    return new Date(close_ts).getTime() < Date.now();
};

export const getMarketProbability = (market: SearchResultMarket): number =>
    market.last_price > 0 ? market.last_price : 50;

export const controversy = (market: MarketVo): number =>
    controversyScore(
        {
            yesBid: marketYesBidCents(market),
            yesAsk: marketYesAskCents(market),
            lastPrice: marketLastPriceCents(market),
        },
        false,
    );

export const mostActiveStatus = (event: EventVo): string => {
    const statuses = new Set(event.markets.map((m) => m.status));
    const statusPriorites: MarketStatus[] = [
        'active',
        'initialized',
        'closed',
        'determined',
        'amended',
        'disputed',
        'settled',
        'finalized',
        'inactive',
        'invalid',
    ];

    for (const status of statusPriorites) {
        if (statuses.has(status)) {
            return status;
        }
    }
    return 'settled';
};

export const numericTarget = (market?: MarketVo, seriesTicker?: string): NumericTarget | null => {
    const unitOrDollarStringToNumber = (unit: string): number => {
        if (unit.charAt(0) === '$') return unitStringToNumber(unit.slice(1));
        return unitStringToNumber(unit);
    };

    if (!market) {
        return null;
    }

    if (!market.sub_title) {
        return null;
    }

    if (
        seriesTicker === 'CPI' ||
        seriesTicker === 'CPICORE' ||
        seriesTicker === 'CPIYOY' ||
        seriesTicker === 'CPICOREYOY'
    ) {
        //always going to be this weird greater than case
        const target = unitOrDollarStringToNumber(market.sub_title.slice(6));
        if (!isNaN(target)) {
            return {
                lowerBound: target + 0.05,
                upperBound: null,
            };
        }
    }

    if (seriesTicker === 'FED') {
        //always going to be this weird greater than case
        const target = unitOrDollarStringToNumber(market.sub_title.slice(6));
        if (!isNaN(target)) {
            return {
                lowerBound: target + 0.125,
                upperBound: null,
            };
        }
    }

    if (market.sub_title[0] === '>') {
        const target = unitOrDollarStringToNumber(market.sub_title.slice(1));
        if (!isNaN(target)) {
            return {
                lowerBound: target,
                upperBound: null,
            };
        }
    } else if (market.sub_title.slice(-9) === 'or higher' || market.sub_title.slice(-8) === 'or above') {
        const target = unitOrDollarStringToNumber(market.sub_title.slice(0, -9));
        if (!isNaN(target)) {
            return {
                lowerBound: target,
                upperBound: null,
            };
        }
    } else if (market.sub_title.slice(0, 6) === 'Above ') {
        const target = unitOrDollarStringToNumber(market.sub_title.slice(6));
        if (!isNaN(target)) {
            return {
                lowerBound: target,
                upperBound: null,
            };
        }
    } else if (market.sub_title.slice(0, 14) === 'Price to beat:') {
        const target = unitOrDollarStringToNumber(market.sub_title.slice(14));
        if (!isNaN(target)) {
            return {
                lowerBound: target,
                upperBound: null,
            };
        }
    } else if (market.sub_title.slice(0, 13) === 'Target price:') {
        const target = unitOrDollarStringToNumber(market.sub_title.slice(13));
        if (!isNaN(target)) {
            return {
                lowerBound: target,
                upperBound: null,
            };
        }
    } else if (market.sub_title.slice(0, 10) === 'More than ') {
        const target = unitOrDollarStringToNumber(market.sub_title.slice(10));
        if (!isNaN(target)) {
            return {
                lowerBound: target,
                upperBound: null,
            };
        }
    } else if (market.sub_title.slice(0, 9) === 'At least ') {
        const target = unitOrDollarStringToNumber(market.sub_title.slice(9));
        if (!isNaN(target)) {
            return {
                lowerBound: target,
                upperBound: null,
            };
        }
    } else if (market.sub_title[0] === '<') {
        const target = unitOrDollarStringToNumber(market.sub_title.slice(1));
        if (!isNaN(target)) {
            return {
                lowerBound: null,
                upperBound: target,
            };
        }
    } else if (market.sub_title.slice(0, 6) === 'Below ') {
        const target = unitOrDollarStringToNumber(market.sub_title.slice(6));
        if (!isNaN(target)) {
            return {
                lowerBound: null,
                upperBound: target,
            };
        }
    } else if (market.sub_title.slice(-8) === 'or lower' || market.sub_title.slice(-8) === 'or below') {
        const target = unitOrDollarStringToNumber(market.sub_title.slice(0, -8));
        if (!isNaN(target)) {
            return {
                lowerBound: null,
                upperBound: target,
            };
        }
    } else if (market.sub_title.includes('Between') && market.sub_title.includes('-')) {
        const workingSub = market.sub_title.split('Between ')[1];
        const targets = workingSub.split('-').map((s) => unitOrDollarStringToNumber(s));
        if (!isNaN(targets[0]) && !isNaN(targets[1])) {
            return {
                lowerBound: targets[0],
                upperBound: targets[1],
            };
        }
    } else if (market.sub_title.includes('-')) {
        const targets = market.sub_title.split('-').map((s) => unitOrDollarStringToNumber(s));
        if (!isNaN(targets[0]) && !isNaN(targets[1])) {
            return {
                lowerBound: targets[0],
                upperBound: targets[1],
            };
        }
    } else if (market.sub_title.includes(' to ')) {
        const targets = market.sub_title.split(' to ').map((s) => unitOrDollarStringToNumber(s));
        if (!isNaN(targets[0]) && !isNaN(targets[1])) {
            return {
                lowerBound: targets[0],
                upperBound: targets[1],
            };
        }
    }

    // Check for an exact target.
    const target = unitOrDollarStringToNumber(market.sub_title);
    if (!isNaN(target)) {
        return {
            lowerBound: target,
            upperBound: target,
        };
    }

    return null;
};

export const shrunkMarkets = (markets: MarketVo[], shrunkSize = 3): MarketVo[] => {
    const isDetermined = markets.filter((m) => m.result !== '').length > markets.length / 2;

    return isDetermined
        ? [...markets]
              .reverse()
              .sort((a, b) => marketVolumeContracts(b) - marketVolumeContracts(a))
              .sort((a, b) => controversy(b) - controversy(a))
              .sort((a, b) => ((a.result === 'yes') === (b.result === 'yes') ? 0 : b.result === 'yes' ? 1 : -1))
              .slice(0, shrunkSize)
        : markets
              .sort((a, b) => marketVolumeContracts(b) - marketVolumeContracts(a))
              .sort((a, b) => controversy(b) - controversy(a))
              .sort((a, b) =>
                  (a.status === 'active') === (b.status === 'active') ? 0 : (b.status === 'active') === true ? 1 : -1,
              )
              .slice(0, shrunkSize);
};

/**
 * Whether a `/markets/...` event page should render `noindex`.
 *
 * The rule itself lives in `constants/marketPageIndexation`, a dependency-free
 * CommonJS leaf, because `sitemapper.js` decides the same question about the same
 * events at deploy time and has to `require()` it under bare `node`. This wrapper
 * exists only to own the one thing that cannot move there: the read of
 * `NEXT_PUBLIC_BUILD_SITEMAP`, which Next inlines at build time and only does so in
 * modules it compiles.
 */
export const shouldNoIndexMarketPage = (eventVo: EventVo, series: SeriesVo): boolean =>
    applyMarketPageIndexationRule(eventVo, series, {
        isIndexableEnv: process.env.NEXT_PUBLIC_BUILD_SITEMAP === 'true',
    });
